/**
 * Resource Types Settings
 *
 * Workspace admins manage the list of resource types used across the Planning Engine.
 * Data is persisted in public.planning_resource_type_overrides (migration 546).
 */

import { useState, useCallback, useEffect } from 'react';
import { Plus, Trash2, Pencil, Lock, Check, X, Users } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import {
  useResourceTypesStore,
  BUILTIN_RESOURCE_TYPES,
  type ResourceTypeDefinition,
} from '../../../stores/prioritization/resourceTypesStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';

function slugify(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/_+/g, '_');
}

function TypeCard({
  type,
  onToggle,
  onEdit,
  onDelete,
}: {
  type: ResourceTypeDefinition;
  onToggle: (key: string, enabled: boolean) => void;
  onEdit?: (key: string) => void;
  onDelete?: (key: string) => void;
}) {
  return (
    <div
      className={`flex items-start justify-between gap-4 px-4 py-3  border transition-colors ${
        type.enabled
          ? 'bg-[var(--bg-raised)] border-[var(--rule)]'
          : 'bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50 border-[var(--rule)] opacity-60'
      }`}
    >
      <div className="flex items-start gap-3 min-w-0">
        <button
          onClick={() => onToggle(type.key, !type.enabled)}
          className={`mt-0.5 w-8 h-5 rounded-full transition-colors flex-shrink-0 relative ${
            type.enabled ? 'bg-[var(--slate)]' : 'bg-[var(--rule-bright)]'
          }`}
          title={type.enabled ? 'Disable' : 'Enable'}
        >
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-[var(--bg-raised)] shadow transition-transform ${type.enabled ? 'translate-x-3' : 'translate-x-0'}`} />
        </button>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[var(--text-pri)]">{type.label}</span>
            {type.builtin && (
              <span className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)]">
                <Lock className="h-3 w-3" /> Built-in
              </span>
            )}
          </div>
          {type.description && <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{type.description}</p>}
          <p className="text-xs text-[var(--text-ter)] font-mono mt-0.5">key: {type.key}</p>
        </div>
      </div>
      {!type.builtin && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {onEdit && (
            <button onClick={() => onEdit(type.key)} className="p-1.5 rounded text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors" title="Edit">
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {onDelete && (
            <button onClick={() => onDelete(type.key)} className="p-1.5 rounded text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20 transition-colors" title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AddTypeForm({ onAdd, onCancel }: { onAdd: (label: string, description: string) => void; onCancel: () => void }) {
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) { setError('Name is required.'); return; }
    onAdd(trimmed, description.trim());
  };

  return (
    <form onSubmit={handleSubmit} className="border border-[var(--slate)] dark:border-[var(--slate)] p-4 bg-[var(--slate-dim)]/50 dark:bg-[var(--slate)]/10">
      <p className="text-sm font-medium text-[var(--text-pri)] mb-3">New custom resource type</p>
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Name</label>
          <input autoFocus type="text" value={label} onChange={(e) => { setLabel(e.target.value); setError(''); }}
            placeholder="e.g. Infrastructure"
            className="w-full text-sm px-3 py-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          {label && <p className="text-xs text-[var(--text-ter)] mt-0.5 font-mono">key: {slugify(label) || '—'}</p>}
          {error && <p className="text-xs text-[var(--coral)] mt-0.5">{error}</p>}
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Description <span className="font-normal text-[var(--text-ter)]">(optional)</span></label>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description of this team or skill type"
            className="w-full text-sm px-3 py-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button type="submit" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--slate)] rounded hover:bg-[var(--slate)] transition-colors">
          <Check className="h-3.5 w-3.5" /> Add
        </button>
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors">
          <X className="h-3.5 w-3.5" /> Cancel
        </button>
      </div>
    </form>
  );
}

function EditTypeForm({ type, onSave, onCancel }: { type: ResourceTypeDefinition; onSave: (label: string, description: string) => void; onCancel: () => void }) {
  const [label, setLabel] = useState(type.label);
  const [description, setDescription] = useState(type.description ?? '');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) { setError('Name is required.'); return; }
    onSave(trimmed, description.trim());
  };

  return (
    <form onSubmit={handleSubmit} className="border border-[var(--slate)] dark:border-[var(--slate)] p-4 bg-[var(--slate-dim)]/50 dark:bg-[var(--slate)]/10">
      <p className="text-sm font-medium text-[var(--text-pri)] mb-3">Edit resource type</p>
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Name</label>
          <input autoFocus type="text" value={label} onChange={(e) => { setLabel(e.target.value); setError(''); }}
            className="w-full text-sm px-3 py-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          {error && <p className="text-xs text-[var(--coral)] mt-0.5">{error}</p>}
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Description <span className="font-normal text-[var(--text-ter)]">(optional)</span></label>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
            className="w-full text-sm px-3 py-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button type="submit" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--slate)] rounded hover:bg-[var(--slate)] transition-colors">
          <Check className="h-3.5 w-3.5" /> Save
        </button>
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors">
          <X className="h-3.5 w-3.5" /> Cancel
        </button>
      </div>
    </form>
  );
}

export default function ResourceTypesSettings() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId ?? '');
  const { load, getAllTypes, toggleBuiltin, addCustomType, updateCustomType, deleteCustomType, loading, error } = useResourceTypesStore();

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [deleteConfirmKey, setDeleteConfirmKey] = useState<string | null>(null);
  const [addError, setAddError] = useState('');

  useEffect(() => {
    if (workspaceId) void load(workspaceId);
  }, [workspaceId, load]);

  const allTypes = getAllTypes();
  const builtins = allTypes.filter((t) => t.builtin);
  const custom = allTypes.filter((t) => !t.builtin);
  const enabledCount = allTypes.filter((t) => t.enabled).length;

  const handleAdd = useCallback(async (label: string, description: string) => {
    const key = slugify(label);
    if (!key) { setAddError('Name produces an empty key.'); return; }
    try {
      await addCustomType(workspaceId, { key, label, description: description || undefined });
      setShowAddForm(false);
      setAddError('');
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add resource type.');
    }
  }, [addCustomType, workspaceId]);

  const handleEdit = useCallback(async (key: string, label: string, description: string) => {
    await updateCustomType(workspaceId, key, { label, description: description || undefined });
    setEditingKey(null);
  }, [updateCustomType, workspaceId]);

  const handleDelete = useCallback(async (key: string) => {
    await deleteCustomType(workspaceId, key);
    setDeleteConfirmKey(null);
  }, [deleteCustomType, workspaceId]);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader icon={<Users className="h-4 w-4 text-[var(--text-ter)]" />} iconBg="bg-[var(--slate-dim)]" title="Resource Types" subtitle="Loading…" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          icon={<Users className="h-4 w-4 text-[var(--text-ter)]" />}
          iconBg="bg-[var(--slate-dim)]"
          title="Resource Types"
          subtitle={`Define team and skill types used across the Planning Engine — ${enabledCount} of ${allTypes.length} enabled.`}
        />
      </div>

      {error && (
        <div className="border border-[var(--coral)] dark:border-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 px-4 py-3">
          <p className="text-sm text-[var(--coral)] dark:text-[var(--coral)]">{error}</p>
        </div>
      )}

      <div className="border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 px-4 py-3">
        <p className="text-sm text-[var(--text-sec)] font-medium mb-1">Where resource types are used</p>
        <ul className="text-xs text-[var(--text-sec)] space-y-0.5 list-disc list-inside">
          <li><strong>Priority resource type</strong> — which team is primarily responsible for a ranked item</li>
          <li><strong>Work unit work profile</strong> — which skill type is needed to execute a work unit</li>
          <li><strong>Capacity model rows</strong> — weekly hours per resource type in a scenario</li>
        </ul>
        <p className="text-xs text-[var(--text-sec)] mt-2">Disabling a type hides it from dropdowns but does not remove it from existing records.</p>
      </div>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">
          Built-in Types <span className="font-normal normal-case text-[var(--text-ter)]">({BUILTIN_RESOURCE_TYPES.length})</span>
        </h2>
        <div className="space-y-2">
          {builtins.map((type) => (
            <TypeCard key={type.key} type={type} onToggle={(key, enabled) => void toggleBuiltin(workspaceId, key, enabled)} />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">
            Custom Types <span className="font-normal normal-case text-[var(--text-ter)]">({custom.length})</span>
          </h2>
          {!showAddForm && (
            <button onClick={() => setShowAddForm(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors">
              <Plus className="h-3.5 w-3.5" /> Add type
            </button>
          )}
        </div>

        {addError && <p className="text-sm text-[var(--coral)]">{addError}</p>}

        {showAddForm && <AddTypeForm onAdd={handleAdd} onCancel={() => { setShowAddForm(false); setAddError(''); }} />}

        {custom.length === 0 && !showAddForm ? (
          <div className="border border-dashed border-[var(--rule)] px-4 py-6 text-center">
            <p className="text-sm text-[var(--text-ter)]">No custom resource types yet.</p>
            <p className="text-xs text-[var(--text-ter)] mt-1">Add one above to supplement the built-in list.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {custom.map((type) =>
              editingKey === type.key ? (
                <EditTypeForm key={type.key} type={type} onSave={(label, description) => void handleEdit(type.key, label, description)} onCancel={() => setEditingKey(null)} />
              ) : deleteConfirmKey === type.key ? (
                <div key={type.key} className="flex items-center justify-between gap-4 px-4 py-3 border border-[var(--coral)] dark:border-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/10">
                  <p className="text-sm text-[var(--coral)] dark:text-[var(--coral)]">Delete <strong>{type.label}</strong>? This won't affect existing records.</p>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => void handleDelete(type.key)} className="px-3 py-1.5 text-xs font-medium text-white bg-[var(--coral)] rounded hover:bg-[var(--coral)] transition-colors">Delete</button>
                    <button onClick={() => setDeleteConfirmKey(null)} className="px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors">Cancel</button>
                  </div>
                </div>
              ) : (
                <TypeCard
                  key={type.key}
                  type={type}
                  onToggle={(key, enabled) => void updateCustomType(workspaceId, key, { enabled })}
                  onEdit={setEditingKey}
                  onDelete={setDeleteConfirmKey}
                />
              ),
            )}
          </div>
        )}
      </section>
    </div>
  );
}
